import {
  Button,
  DialogActionTrigger,
  ButtonGroup,
  DialogTitle,
  Input,
  Box,
  Icon,
  Text,
  Grid,
  VStack,
  Flex,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { useState, useEffect } from "react";
import { FaExchangeAlt } from "react-icons/fa";
import {
  FiFileText,
  FiHash,
  FiCalendar,
  FiList,
  FiUpload,
  FiPlus,
  FiTrash,
} from "react-icons/fi";
import { TableFieldsService } from "../../client";
import type {
  ApiError,
  TableFieldUpdate,
  FieldType,
  AllowedExt,
  TableFieldPublic,
} from "../../client";
import useCustomToast from "../../hooks/useCustomToast";
import { handleError } from "../../utils";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "../ui/dialog";
import { Field } from "../ui/field";
import { Checkbox } from "../ui/checkbox";
import { Select } from "../ui/select";

interface EditTableFieldProps {
  tableField: TableFieldPublic;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEditComplete?: (updatedField: TableFieldPublic) => void;
}

const EditTableField = ({
  tableField,
  isOpen: externalIsOpen,
  onOpenChange: externalOnOpenChange,
  onEditComplete,
}: EditTableFieldProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessToast } = useCustomToast();

  // 외부에서 제어하는 경우와 내부에서 제어하는 경우를 구분
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalOnOpenChange || setInternalIsOpen;

  // 현재 선택된 필드 타입, 숫자/문자열 타입 저장용 상태값
  const [selectedFieldType, setSelectedFieldType] = useState("");
  const [selectedNumberType, setSelectedNumberType] = useState("");
  const [selectionElements, setSelectionElements] = useState([
    { label: "", color: "#000000" },
  ]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid, isSubmitting },
  } = useForm<TableFieldUpdate>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      field_name: "",
      description: "",
      field_code: "",
      field_type: "string",
      is_required: true,
    },
  });

  // 기존 필드 데이터로 폼 초기화
  useEffect(() => {
    if (tableField && isOpen) {
      reset({
        field_name: tableField.field_name,
        description: tableField.description,
        field_code: tableField.field_code || "",
        field_type: tableField.field_type,
        is_required: tableField.is_required,
        number_metadata: tableField.number_metadata || {
          number_type: "natural",
          unit: "",
          min_value: 0,
          max_value: 100,
          decimal_round: undefined,
        },
        string_metadata: tableField.string_metadata || {
          string_type: "shortText",
        },
        datetime_metadata: tableField.datetime_metadata || {
          datetime_type: "datetime",
        },
        selection_metadata: tableField.selection_metadata || { options: [] },
        file_metadata: tableField.file_metadata || {
          allowed_extension: "pdf",
          max_file_size_mb: 10,
        },
      } as TableFieldUpdate);

      // 필드 타입 상태 설정
      setSelectedFieldType(tableField.field_type);

      // 숫자 타입 설정
      if (tableField.number_metadata) {
        setSelectedNumberType(tableField.number_metadata.number_type);
      }

      // 선택 요소 설정
      if (tableField.selection_metadata?.options) {
        setSelectionElements(
          tableField.selection_metadata.options as {
            label: string;
            color: string;
          }[]
        );
      }
    }
  }, [tableField, isOpen, reset]);

  const addSelectionElement = () => {
    setSelectionElements((prev) => [...prev, { label: "", color: "#000000" }]);
  };

  const removeSelectionElement = (index: number) => {
    setSelectionElements((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSelectionChange = (
    index: number,
    key: "label" | "color",
    value: string
  ) => {
    setSelectionElements((prev) => {
      const updated = [...prev];
      updated[index][key] = value;
      return updated;
    });
  };

  const mutation = useMutation({
    mutationFn: (data: TableFieldUpdate) =>
      TableFieldsService.updateTableField({
        id: tableField.id,
        requestBody: data,
      }),
    onSuccess: (updatedField) => {
      showSuccessToast("테이블 필드가 성공적으로 수정되었습니다.");
      reset();
      setIsOpen(false);
      setHasSubmitted(false);
      onEditComplete?.(updatedField);
    },
    onError: (err: ApiError) => {
      handleError(err);
      setHasSubmitted(false);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "tableFields",
      });
    },
  });

  const onSubmit: SubmitHandler<TableFieldUpdate> = (data) => {
    if (hasSubmitted) return;
    setHasSubmitted(true);

    // 선택된 필드 타입에 따라 해당 메타데이터만 포함시키고 나머지는 null로 처리
    const filteredData: TableFieldUpdate = {
      ...data,
      number_metadata:
        data.field_type === "number" ? data.number_metadata : null,
      string_metadata:
        data.field_type === "string" ? data.string_metadata : null,
      datetime_metadata:
        data.field_type === "datetime" ? data.datetime_metadata : null,
      selection_metadata:
        data.field_type === "selection" ? { options: selectionElements } : null,
      file_metadata: data.field_type === "file" ? data.file_metadata : null,
    };

    // 필드 수정 API 호출
    mutation.mutate(filteredData);
  };

  const fieldTypes = [
    { type: "string", icon: FiFileText, label: "문자열" },
    { type: "number", icon: FiHash, label: "숫자" },
    { type: "datetime", icon: FiCalendar, label: "날짜/시간" },
    { type: "selection", icon: FiList, label: "선택" },
    { type: "file", icon: FiUpload, label: "파일" },
  ];

  const AllowedExtList: AllowedExt[] = ["pdf", "ppt", "jpg", "xlsx"];

  return (
    <DialogRoot
      size={{ base: "xs", md: "lg" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      {!externalOnOpenChange && (
        <DialogTrigger asChild>
          <Button variant="ghost">
            <FaExchangeAlt fontSize="16px" />
            테이블 필드 수정
          </Button>
        </DialogTrigger>
      )}
      <DialogContent asChild>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>테이블 필드 수정</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <VStack gap={4} align="stretch">
              <Field
                required
                invalid={!!errors.field_name}
                errorText={errors.field_name?.message}
                label="필드명"
              >
                <Input
                  id="field_name"
                  {...register("field_name", {
                    required: "필드명을 입력하세요.",
                  })}
                  placeholder="필드명을 입력하세요."
                  type="text"
                />
              </Field>

              <Field
                required
                invalid={!!errors.description}
                errorText={errors.description?.message}
                label="설명"
              >
                <Input
                  id="description"
                  {...register("description", {
                    required: "설명을 입력하세요.",
                  })}
                  placeholder="설명을 입력하세요."
                  type="text"
                />
              </Field>

              <Field
                invalid={!!errors.field_code}
                errorText={errors.field_code?.message}
                label="코드명"
              >
                <Input
                  id="field_code"
                  {...register("field_code")}
                  placeholder="CSV/XLSX 파일의 필드명을 입력하세요 (예: name)"
                  type="text"
                />
              </Field>

              <Field required label="필드 타입">
                <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                  {fieldTypes.map((fieldType) => (
                    <Box
                      key={fieldType.type}
                      onClick={() => {
                        const type = fieldType.type as FieldType;
                        setSelectedFieldType(type);
                        setValue("field_type", type);
                      }}
                      p={3}
                      borderWidth={2}
                      borderColor={
                        selectedFieldType === fieldType.type
                          ? "blue.500"
                          : "gray.200"
                      }
                      borderRadius="md"
                      cursor="pointer"
                      textAlign="center"
                      _hover={{ borderColor: "blue.300" }}
                    >
                      <Icon as={fieldType.icon} w={5} h={5} />
                      <Text mt={2} fontSize="sm">
                        {fieldType.label}
                      </Text>
                    </Box>
                  ))}
                </Grid>
              </Field>

              {selectedFieldType === "number" && (
                <>
                  <Field required label="숫자 타입">
                    <Controller
                      name="number_metadata.number_type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          name="numberType"
                          placeholder="숫자 유형 선택"
                          value={field.value}
                          onChange={(val) => {
                            setSelectedNumberType(val);
                            field.onChange(val);
                          }}
                          options={[
                            { label: "자연수", value: "natural" },
                            { label: "정수", value: "integer" },
                            { label: "소수", value: "decimal" },
                          ]}
                          isRequired
                          portalled={false}
                        />
                      )}
                    />
                  </Field>

                  <Field required label="단위">
                    <Input
                      id="unit"
                      {...register("number_metadata.unit")}
                      placeholder="단위를 입력하세요 (예. kg, cm)"
                    />
                  </Field>

                  <Field required label="최소값">
                    <Input
                      id="min_value"
                      {...register("number_metadata.min_value", {
                        valueAsNumber: true,
                      })}
                      placeholder="최소값을 입력하세요"
                    />
                  </Field>

                  <Field required label="최대값">
                    <Input
                      id="max_value"
                      {...register("number_metadata.max_value", {
                        valueAsNumber: true,
                      })}
                      placeholder="최대값을 입력하세요"
                    />
                  </Field>

                  {selectedNumberType === "decimal" && (
                    <Field label="반올림 소수점">
                      <Input
                        id="decimal_round"
                        {...register("number_metadata.decimal_round", {
                          valueAsNumber: true,
                        })}
                        placeholder="반올림 소수점을 입력하세요"
                      />
                    </Field>
                  )}
                </>
              )}

              {selectedFieldType === "datetime" && (
                <Field required label="날짜/시간 유형">
                  <Controller
                    name="datetime_metadata.datetime_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        name="datetimeType"
                        placeholder="날짜/시간 유형 선택"
                        value={field.value}
                        onChange={(val) => {
                          field.onChange(val);
                        }}
                        options={[
                          { label: "날짜/시간", value: "datetime" },
                          { label: "날짜", value: "date" },
                          { label: "시간", value: "time" },
                        ]}
                        isRequired
                        portalled={false}
                      />
                    )}
                  />
                </Field>
              )}

              {selectedFieldType === "selection" && (
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    선택 요소
                  </Text>
                  {selectionElements.map((element, index) => (
                    <Flex
                      key={`selection-${index}-${element.label}`}
                      mb={2}
                      alignItems="center"
                      gap={3}
                    >
                      <Input
                        value={String(element.label)}
                        placeholder={`라벨 ${index + 1}`}
                        onChange={(e) =>
                          handleSelectionChange(index, "label", e.target.value)
                        }
                        borderColor={
                          selectionElements.findIndex(
                            (el, i) => el.label === element.label && i !== index
                          ) !== -1
                            ? "red.500"
                            : undefined
                        }
                      />
                      <Input
                        type="color"
                        value={String(element.color)}
                        onChange={(e) =>
                          handleSelectionChange(index, "color", e.target.value)
                        }
                        title="Pick a color"
                        width="50px"
                        p={0}
                      />
                      <Icon
                        as={FiTrash}
                        color="red.500"
                        cursor="pointer"
                        onClick={() => removeSelectionElement(index)}
                      />
                    </Flex>
                  ))}
                  <Button onClick={addSelectionElement} mt={2}>
                    <Icon as={FiPlus} mr={2} />
                    요소 추가
                  </Button>
                </Box>
              )}

              {selectedFieldType === "file" && (
                <>
                  <Field required label="파일 확장자">
                    <Controller
                      name="file_metadata.allowed_extension"
                      control={control}
                      render={({ field }) => (
                        <Select
                          name="allowedExtension"
                          placeholder="허용 확장자 선택"
                          value={field.value}
                          onChange={field.onChange}
                          options={AllowedExtList.map((ext) => ({
                            label: ext.toUpperCase(),
                            value: ext,
                          }))}
                          isRequired
                          portalled={false}
                        />
                      )}
                    />
                  </Field>
                  <Field required label="파일당 최대 크기(MB)">
                    <Input
                      type="number"
                      min={1}
                      {...register("file_metadata.max_file_size_mb", {
                        valueAsNumber: true,
                      })}
                      placeholder="예: 10"
                    />
                  </Field>
                </>
              )}

              <Controller
                name="is_required"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="is_required"
                    checked={!!field.value}
                    onCheckedChange={(details) => {
                      field.onChange(details.checked);
                    }}
                  >
                    필수항목
                  </Checkbox>
                )}
              />
            </VStack>
          </DialogBody>

          <DialogFooter gap={3}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  disabled={isSubmitting}
                >
                  닫기
                </Button>
              </DialogActionTrigger>
              <Button
                variant="solid"
                type="submit"
                loading={isSubmitting}
                disabled={!isValid}
              >
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>

          <DialogCloseTrigger />
        </form>
      </DialogContent>
    </DialogRoot>
  );
};

export default EditTableField;
